import React, { FunctionComponent, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import produce from 'immer';
import cn from 'classnames';

import { UpdatePageModel } from '../UpdatePageModel';
import { Alert, DateTimeRange, ErrorMessage, FormHeading, StageStateIcon, TimeDurationPicker } from 'components';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader} from '@cfa/react-components';
import { AsyncStatus, StageState, UpdateState, UserRole } from 'enums';
import { IStage } from 'interfaces';
import { parseAxiosError, UsersService } from 'services';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLuxonTimezone } from 'hooks';
import { DateTime } from 'luxon';
import { StageMenu } from './StageMenu';

interface IStageForm {
    // Our date range control needs separate dates for date/time.
    startDate: Date,
    startTime: Date,
    stopDate: Date,
    stopTime: Date,
}

interface IStageModalProps {
    isNew: boolean,
    model: UpdatePageModel,
    show: boolean,
    stage: IStage,
    stageIndex: number,
    onOpenDeleteModal: (stage: IStage) => void,
    onHide: () => void,
    onConfirm: () => void,
}

interface IStageModalState {
    saveError?: string,
    saveStatus: AsyncStatus,
    saveType?: 'save' | 'delete',

    // Stage fields
    isConfirmed: boolean,
    notes: string,
    requiresConfirm: boolean,
    start: Date,
    stop: Date,
}

enum Actions {
    SnapStartToPrev = 'SnapStartToPrev',
    SnapEndToNext = 'SnapEndToNext',
}

export const StageModal: FunctionComponent<IStageModalProps> = function FilterModal(props) {
    if (props.model.loadStatus !== AsyncStatus.Success || !props.stage) return null;

    const { isNew, model, show, stage, stageIndex, onOpenDeleteModal, onHide, onConfirm } = props;
    const [state, setState] = useState<IStageModalState>(() => {
        // Copy the stage props so we don't modify the original.
        return {
            saveStatus: AsyncStatus.Initial,

            isConfirmed: stage.isConfirmed,
            notes: stage.notes,
            requiresConfirm: stage.requiresConfirmation,
            start: stage.startTime && new Date(stage.startTime.getTime()),
            stop: stage.stopTime && new Date(stage.stopTime.getTime()),
        };
    });
    const { saveError, saveStatus, saveType, isConfirmed, notes, requiresConfirm, start, stop } = state;
    const { update } = model;

    const form = useForm<IStageForm>({
        mode: 'onTouched',
        defaultValues: {
            startDate: start,
            startTime: start && new Date(start.getTime()),
            stopDate: stop,
            stopTime: stop && new Date(stop.getTime()),
        },
    });

    const { timezone } = useLuxonTimezone(UsersService.me.timezone);

    function hashDateDay(d: Date): string {
        return `${d.getDate()}-${d.getMonth()}-${d.getFullYear()}`;
    }

    // We want to show other stages on the calendar. We can control the date render function for the calendar.
    // So, we'll generate a lookup map so that we can check if a date is part of another stage.
    const otherStageDates = useMemo(() => {
        const out: {[dateHash: string]: {isStart: boolean, isEnd: boolean}} = {};

        try {
            for (let i = 0; i < model.update.rollout.stages.length; i++) {
                // Skip our stage
                if (i === stageIndex) continue;

                const stage = model.update.rollout.stages[i];
                
                // This is used within the ant picker, which doesn't support timezones, so we need to apply them.
                const stageStartDt = DateTime
                    .fromJSDate(stage.startTime, {zone: timezone})
                    .setZone('local', {keepLocalTime: true});
                const stageStopDt = DateTime
                    .fromJSDate(stage.stopTime, {zone: timezone})
                    .setZone('local', {keepLocalTime: true});

                // Generate a date entry for each day in the stage
                let isStart = true;
                let hash: string;
                for (let cur = stageStartDt.startOf('day'); cur <= stageStopDt; cur = cur.plus({days: 1})) {
                    // Check if we already have an entry for this date. If we do, we don't want to reset "isStart" and "isEnd",
                    // so we're only ever setting them to true.
                    hash = hashDateDay(cur.toJSDate());
                    let entry = out[hash] || {isStart: false, isEnd: false};

                    if (isStart) {
                        entry.isStart = true;
                        isStart = false;
                    }

                    out[hash] = entry;
                }
                // Mark the last date as the end.
                if (hash) {
                    out[hash].isEnd = true;
                }
            }
        } catch(err) {
            console.error('Error generating dates for other stages: ', err);
        }

        return out;
    }, [model.update])

    const onDateRangeChange = (newStartDate: Date, newStopDate: Date) => {
        setState(produce(draft => {
            draft.start = newStartDate;
            draft.stop = newStopDate;
        }));
    }

    function onChangeDuration(newDurationMilli: number) {
        if (start) {
            // Add the duration to the start to calculate the new stop.
            const newStopMilli = start.getTime() + newDurationMilli;

            setState(produce(draft => {
                draft.stop = new Date(newStopMilli);
            }));
        }
    }

    function onDateAction(e) {
        switch (e.target.value) {
            case Actions.SnapStartToPrev: {
                // Lookup the previous stage
                const prev = model.update.rollout.stages[stageIndex - 1];
                if (!prev) return;

                const prevStopMilli = prev.stopTime.getTime();
                setState(produce(draft => {
                    draft.start = new Date(prevStopMilli);
                    draft.stop = new Date(prevStopMilli + durationMilliseconds);
                }));
                break;
            }
            case Actions.SnapEndToNext: {
                // Lookup the next stage
                const next = model.update.rollout.stages[stageIndex + 1];
                if (!next) return;

                const nextStartMilli = next.startTime.getTime();
                setState(produce(draft => {
                    draft.start = new Date(nextStartMilli - durationMilliseconds);
                    draft.stop = new Date(nextStartMilli);
                }));
                break;
            }
        }
    }

    function onChangeRequiresConfirm(e) {
        const checked = e.target.checked;
        setState(produce(draft => {
            draft.requiresConfirm = checked;
        }));
    }

    function onChangeNotes(e) {
        const val = e.target.value;

        setState(produce(draft => {
            draft.notes = val;
        }));
    }

    function onStartConfirm() {
        onHide();
        onConfirm();
    }

    async function onSave() {
        const onError = (errMessage: string) => {
            setState(produce(draft => {
                draft.saveStatus = AsyncStatus.Error;
                draft.saveError = errMessage;
            }))
        }

        // Submit form to trigger validation
        form.handleSubmit(() => {
            // The UI only shows seconds, so it can look weird if the milliseconds are slightly off,
            // which can make the duration not quite match the times.
            start.setMilliseconds(0);
            stop.setMilliseconds(0);

            // Build a new stage object from our state
            const stage: IStage = {
                // Copy props that we aren't modifying
                ...props.stage,

                // Set props that we're modifying from our state
                notes: notes,
                startTime: start,
                stopTime: stop,
                requiresConfirmation: requiresConfirm,
            }

            // Save
            setState(produce(draft => {
                draft.saveStatus = AsyncStatus.Loading;
                draft.saveError = '';
                draft.saveType = 'save';
            }))

            let savePromise: Promise<any>;
            if (model.isChangingSchedule) {
                savePromise = model.changeStageScheduleLocalOnly(stage)
            } else {
                savePromise = model.saveStage(stage)
            }

            savePromise.then(onHide)
                .catch((err) => {
                    onError(parseAxiosError(err));
                });
        })();
    }

    function onDeleteInternal() {
        onHide();
        if (isNew) return;

        onOpenDeleteModal(stage);
    }

    let durationMilliseconds = 0;
    if (start && stop) {
        durationMilliseconds = Math.max(0, stop.getTime() - start.getTime());
    }

    const hasEditPerm = UsersService.oktaUserInfo?.role >= UserRole.LimitedUser;
    const isDraft = update.state === UpdateState.Draft
    const isChangingSchedule = model.isChangingSchedule && stage.state === StageState.Scheduled;

    return (
        <Modal show={show} onClose={onHide} size="lg" className="stageModal" scrollMode='modal-body'>
            <ModalHeader >
                {isNew && 'New Stage '}
                {!isNew && (<>
                    <StageStateIcon state={stage.state} />
                    Stage {stageIndex + 1}
                </>)}
                <StageMenu
                    className="modalMenu"
                    canEdit={hasEditPerm}
                    isNewStage={isNew}
                    model={model}
                    stage={stage}
                    onDelete={onDeleteInternal}
                />
            </ModalHeader>
            <ModalBody style={{textAlign: "left"}}>
                <div className="row">
                    <div className="col">
                        <FormHeading>
                            Schedule
                        </FormHeading>
                    </div>
                </div>
                <div className="row">
                    <div className="col">
                        <DateTimeRange<IStageForm>
                            allowClear={false}
                            dateRender={(current, today, info) => {
                                return (<>
                                    <div className="ant-picker-cell-inner">
                                        {DateTime.fromJSDate(current).toLocaleString({day: 'numeric'})}
                                    </div>
                                    {(() => {
                                        const otherStage = otherStageDates[hashDateDay(current)];
                                        if (!otherStage) return null;

                                        return <div className={cn('timepicker_event', {
                                            timepicker_event_start: otherStage.isStart,
                                            timepicker_event_end: otherStage.isEnd,
                                        })}></div>;
                                    })()}
                                </>);
                            }}
                            label="Start / End"
                            nameStartDate="startDate"
                            nameEndDate="stopDate"
                            nameStartTime="startTime"
                            nameEndTime="stopTime"
                            form={form}
                            required={true}
                            disabled={!(hasEditPerm && (isDraft || isChangingSchedule))}
                            start={start}
                            end={stop}
                            timezone={timezone}
                            onChange={onDateRangeChange}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-6">
                        <TimeDurationPicker
                            disabled={!(hasEditPerm && (isDraft || isChangingSchedule))}
                            durationMilliseconds={durationMilliseconds}
                            label="Duration"
                            onChange={onChangeDuration}
                        />
                    </div>
                    <div className="col-4">
                        <div className="formItem">
                            <div className="formItem_label">
                                Actions
                            </div>
                            <select
                                className="formItem_select"
                                disabled={!(hasEditPerm && (isDraft || isChangingSchedule))}
                                onChange={onDateAction}
                                value={''}
                            >
                                <option value={undefined}> </option>
                                <option value={Actions.SnapStartToPrev}>Snap start to previous stage</option>
                                <option value={Actions.SnapEndToNext}>Snap end to next stage</option>
                            </select>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col">
                        <FormHeading>
                            Confirmation
                        </FormHeading>
                    </div>
                </div>
                <div className="row">
                    <div className="col">
                        {stageIndex === 0 && <>
                            For stages after the first one, you can require that a user confirm the stage
                            before it's allowed to start.
                        </>}
                        {stageIndex > 0 && <>
                            <label>
                                <input
                                    type="checkbox"
                                    checked={!!requiresConfirm}
                                    onChange={onChangeRequiresConfirm}
                                    // Note: We don't allow changing this during the schedule change.
                                    disabled={!(hasEditPerm && isDraft)}
                                />
                                Require a user to confirm this stage before it's allowed to start.
                            </label>
                        </>}
                        <ul>
                            <li>This is a manual safety check to confirm that the previous stage succeeded before starting the next one.</li>
                            <li>A user can confirm a stage after the previous stage has started.</li>
                            <li>If a user waits too long to confirm a stage, they may need to adjust the schedule.</li>
                        </ul>
                    </div>
                </div>
                <div className="row">
                    <div className="col">
                        <FormHeading>
                            Notes
                        </FormHeading>
                    </div>
                </div>
                <div className="row">
                    <div className="col">
                        <div className="formItem">
                            <textarea
                                className="formItem_textarea"
                                disabled={!(hasEditPerm && isDraft)}
                                maxLength={500}
                                rows={3}
                                value={notes}
                                onChange={onChangeNotes}
                            />
                        </div>
                    </div>
                </div>
                {saveError && <ErrorMessage message={saveError} />}
                {model.isChangingSchedule && stage.state !== StageState.Scheduled && (
                    <Alert type="info">
                        <FontAwesomeIcon icon="info-circle" />&nbsp;
                        You can only change the schedule on stages that haven't started yet.
                    </Alert>
                )}
            </ModalBody>
            <ModalFooter>
                <Button 
                    variant="outlined" 
                    onClick={onHide}
                    disabled={saveStatus === AsyncStatus.Loading}
                >
                    Close
                </Button>
                {!model.isChangingSchedule && (<>
                    <Button
                        style={{marginLeft: "10px"}}
                        onClick={onSave}
                        loading={saveStatus === AsyncStatus.Loading}
                        disabled={!hasEditPerm || !isDraft || saveStatus === AsyncStatus.Success}
                    >
                        {saveType === 'save' && saveStatus === AsyncStatus.Loading 
                            ? 'Saving'
                            : 'Save'}
                    </Button>
                </>)}
                {model.isChangingSchedule && (
                    <Button
                        style={{marginLeft: "10px"}}
                        onClick={onSave}
                        disabled={!hasEditPerm || stage.state !== StageState.Scheduled}
                    >
                        Accept Schedule
                    </Button>
                )}
                {update.state >= UpdateState.Scheduled && stage.requiresConfirmation && !stage.isConfirmed && (
                    <Button
                        style={{marginLeft: "10px"}}
                        variant="outlined"
                        color="secondary"
                        onClick={onStartConfirm}
                        disabled={!(hasEditPerm && model.canConfirmStage(update, stage, stageIndex))}
                    >
                        Start Confirm Stage
                    </Button>
                )}
            </ModalFooter>
        </Modal>
    );
}